/* What a workspace's own skill shows in place of a catalog skill's GitHub owner, stars
   and audits: who made it, where a GitHub copy came from, and the agents that have it. */
import React from "react";
import { ExternalLink, GitCommitHorizontal, ShieldQuestion, Sparkles } from "lucide-react";

import { Counted, ListCard, ListEmpty, ListRow, ListTile } from "@/shared/components/ListCard";
import { Chip } from "@/shared/components/ui-kit";

import { OwnerAvatar, useCredit } from "./atoms";

import type { SkillDetail } from "@/shared/types";

export function WorkspaceCredits({ skill }: { skill: SkillDetail }) {
  const credit = useCredit();
  const link = "inline-flex items-center gap-1 min-w-0 hover:text-[var(--text-primary)] hover:underline";
  return (
    <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] divide-y divide-[var(--hairline)]">
      <div className="px-4 py-3 flex items-center gap-3">
        <OwnerAvatar owner={skill.owner} size={32} rounded={9} />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium tracking-[-0.005em] truncate">Made by {credit(skill.created_by)}</div>
          <div className="mt-[2px] text-[12px] text-[var(--text-tertiary)]">
            This workspace's own skill · changed <span className="font-mono tabular-nums">{new Date(skill.updated_at).toLocaleDateString()}</span>
          </div>
        </div>
      </div>
      {skill.repo && (
        <div className="px-4 py-3 space-y-1">
          <div className="flex items-center gap-x-3 gap-y-1 flex-wrap text-[12px] text-[var(--text-tertiary)]">
            <span className="text-[var(--text-secondary)]">Copied from GitHub</span>
            {skill.folder_url && (
              <a href={skill.folder_url} target="_blank" rel="noreferrer" className={link}>
                <span className="truncate font-mono">{skill.repo}{skill.folder ? `/${skill.folder}` : ""}</span>
                <ExternalLink className="w-3 h-3 shrink-0" strokeWidth={1.75} />
              </a>
            )}
            {skill.commit && (
              <span className="inline-flex items-center gap-1 font-mono" title={skill.commit}>
                <GitCommitHorizontal className="w-3 h-3" strokeWidth={1.75} />{skill.commit.slice(0, 7)}
              </span>
            )}
            {skill.license && <span className="font-mono">{skill.license}</span>}
          </div>
          <p className="m-0 text-[12px] leading-[1.55] text-[var(--text-tertiary)]">
            A copy taken at that commit: it doesn't follow changes made upstream since.
          </p>
        </div>
      )}
      <p className="m-0 px-4 py-3 flex items-start gap-1.5 text-[12px] leading-[1.55] text-[var(--text-tertiary)]">
        <ShieldQuestion className="w-3.5 h-3.5 shrink-0 mt-[1px]" strokeWidth={1.75} />
        Not audited: skills.sh's security partners check the public catalog only. An agent's owner reads its files before saying yes.
      </p>
    </div>
  );
}

/** The agents that have it, attached or waiting for their owner's yes. */
export function UsedBy({ skill, onOpenAgent }: { skill: SkillDetail; onOpenAgent?: (agentId: string) => void }) {
  return (
    <ListCard label={<>Used by <Counted n={skill.used_by.length} noun="agent" /></>}>
      {skill.used_by.length === 0 && (
        <ListEmpty icon={Sparkles} title="No agent has it yet" hint="Put it on an agent from that agent's Settings, under Skills." />
      )}
      {skill.used_by.map((u) => (
        <ListRow
          key={u.agent_id}
          onClick={onOpenAgent && (() => onOpenAgent(u.agent_id))}
          lead={<ListTile><span className="text-[12px] font-semibold uppercase">{u.name[0]}</span></ListTile>}
          title={u.name}
          desc={u.status === "attached" ? "Attached" : "Asked for: waiting for the agent's owner"}
        >
          {u.status === "requested" && <Chip>Requested</Chip>}
        </ListRow>
      ))}
    </ListCard>
  );
}
